import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI, getCartId, setCartId } from '../lib/api';
import { trackAddToCart } from '../lib/tracking';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    const cartId = getCartId();
    if (cartId) {
      try {
        const response = await cartAPI.get(cartId);
        setCart(response.data);
      } catch (error) {
        // Cart not found, will create new one when adding item
        localStorage.removeItem('cart_id');
        setCart(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const createCart = async () => {
    const response = await cartAPI.create();
    setCartId(response.data.id);
    setCart(response.data);
    return response.data;
  };

  const addItem = async (item) => {
    let currentCart = cart;

    if (!currentCart) {
      currentCart = await createCart();
    }

    const response = await cartAPI.addItem(currentCart.id, item);
    setCart(response.data);

    // Track event
    trackAddToCart({
      id: item.look_id || item.product_id,
      name: item.name || 'Item',
      type: item.look_id ? 'look' : 'product',
      price: response.data.items[response.data.items.length - 1]?.price_cents || 0
    });

    return response.data;
  };

  const updateItem = async (itemId, quantity) => {
    if (!cart) return;

    const response = await cartAPI.updateItem(cart.id, itemId, quantity);
    setCart(response.data);
    return response.data;
  };

  const removeItem = async (itemId) => {
    if (!cart) return;

    const response = await cartAPI.removeItem(cart.id, itemId);
    setCart(response.data);
    return response.data;
  };

  const applyPromo = async (promoCode) => {
    if (!cart) return;
    const response = await cartAPI.applyPromo(cart.id, promoCode);
    setCart(response.data);
    return response.data;
  };

  const clearCart = () => {
    localStorage.removeItem('cart_id');
    setCart(null);
  };

  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addItem,
      updateItem,
      removeItem,
      applyPromo,
      clearCart,
      itemCount,
      isOpen,
      openCart,
      closeCart,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
